import * as React from 'react';
import {
  AirplaneRegular,
  MoneyOffRegular,
  TimerRegular,
  BoxRegular,
  BuildingRegular,
  BuildingMultipleRegular,
  DocumentOnePageRegular,
  ApprovalsAppRegular,
  SettingsCogMultipleRegular
} from "@fluentui/react-icons";
import { Button, Image, makeStyles, Text, tokens, typographyStyles, Carousel, CarouselCard, CarouselNav, CarouselNavButton, CarouselSlider, CarouselAnnouncerFunction, CarouselNavContainer, InfoLabel, Link } from '@fluentui/react-components';
import { bannerCard, centered, flexColumn, flexRow, formStyle, intro, introMobile, wrapperContent, wrapperContentWithBackground } from '../../styles/GlobalStyles';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from '../../state/GlobalStateProvider';
import { useMediaQuery, mobileMaxWidth } from '../../util/ResponsiveDesign';

interface AppCard
{
  text: string,
  subtext?: string;
  link: string,
  buttonText: string,
  styleClass: string
}

const useStyles = makeStyles({
  intro: intro,
  introMobile: introMobile,
  flexRow: flexRow,
  flexColumn: flexColumn,
  cardContent: {
    ...intro,
    ...flexColumn,
    height: "450px",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "auto",
    marginRight: "auto"
  },
  form: formStyle,
  appRoot: wrapperContent,
  content: {
    ...wrapperContent,
    margin: "10px",
    padding: "10px",
    flex: "1",
    boxSizing: "border-box"
  },
  cardContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    position: "absolute",
    left: "10%",
    top: "25%",
    background: tokens.colorNeutralBackground1,
    padding: "18px",
    maxWidth: "270px",
    width: "50%",
  },
  title: {
    ...typographyStyles.title1,
  },
  subintro: {
    ...typographyStyles.subtitle1,
    alignContent: "center",
    borderRadius: tokens.borderRadiusLarge,
    textAlign: "center",
    maxWidth: "50%"
  },
  subtitle: {
    ...typographyStyles.subtitle2Stronger,
    alignContent: "center",
    borderRadius: tokens.borderRadiusLarge,
    textAlign: "center",
    maxWidth: "50%"
  },
  subtext: {
    ...typographyStyles.body1,
  },
  card1: {
    ...bannerCard,
    backgroundImage: "linear-gradient(140deg, rgb(85, 214, 170) 0%, lightgreen 50%, rgb(31, 200, 219) 75%)"
  },
  card2: {
    ...bannerCard,
    backgroundImage: "linear-gradient(140deg, #55d6aa 0%, orange 50%, #1fc8db 75%)"
  },
  card3: {
    ...bannerCard,
    backgroundImage: "linear-gradient(140deg, #55d6aa 0%, pink 50%, #1fc8db 75%)"
  },
  centeredFlexColumn: {
    ...flexColumn,
    marginLeft: "auto",
    marginRight: "auto"
  },
  centeredFlexColumnLimited: {
    ...flexColumn,
    flex: 1,
    marginLeft: "auto",
    marginRight: "auto"
  },
  wrapperContentWithBackground: {
    ...wrapperContentWithBackground,
    margin: "0px",
    flexWrap: "wrap"
  },
  centered: centered
});

const BannerCard: React.FC<{
  children: React.ReactNode;
  card: AppCard;
  index: number;
}> = (props) => {
  const navigate = useNavigate();
  const { children, card, index } = props;
  const classes = useStyles();

  const onButtonClick = React.useCallback(() =>
  {
    navigate(card.link);
  }, [card.link, navigate]);

  return (
    <CarouselCard
      className={card.styleClass}
      aria-label={card.text}
      id={`card-${index}`}
    >
      <div className={classes.cardContainer}>
        <div className={classes.title}>{children}</div>
        { card.subtext &&
          <div className={classes.subtext}>
            { card.subtext }
          </div>
        }
        <div>
          <Button size="small" onClick={onButtonClick} shape="square" appearance="primary">
            {card.buttonText}
          </Button>
        </div>
      </div>
    </CarouselCard>
  );
};

const getAnnouncement: CarouselAnnouncerFunction = (
  index: number,
  totalSlides: number,
  slideGroupList: number[][]
) => {
  return `Carousel slide ${index + 1} of ${totalSlides}`;
};

export const App = () => {
  const classes = useStyles();
  const isMobile = useMediaQuery(mobileMaxWidth);

  const navigate = useNavigate();

  const globalState = useGlobalState();

  const cards: Array<AppCard> = React.useMemo<Array<AppCard>>(() => [
    {
      text: "Ianus Guard - A modern toolkit for license management",
      buttonText: "See how it works",
      styleClass: classes.card1,
      link: "/docs/general"
    },
    {
      text: "Ianus Guard for Publishers",
      buttonText: "Learn more",
      styleClass: classes.card2,
      link: "/docs/publisher"
    },
    {
      text: "Ianus Guard for Customers",
      buttonText: "Learn more",
      styleClass: classes.card3,
      link: "/docs/customer"
    }
  ], [classes.card1, classes.card2, classes.card3]);

  return (
    <div id="appRoot" className={classes.appRoot}>
      <div id="appContent" className={classes.content}>
        <div className={classes.flexColumn}>
          <div className={classes.form} style={{ alignItems: "center" }}>
            <Text className={isMobile? classes.introMobile : classes.intro}>
              Simple & Flexible License Management
            </Text>

            <Text className={classes.subintro}>
              Trusted by developers. Built for everyone.
            </Text>

            <Text className={classes.subtitle}>
              Issue, validate and manage software licenses in minutes - no servers, no lock-in and full control.
              <br />
              Seamless integration into your products.
              <br />
              Secure. Robust. Easy. Pick three.
            </Text>

            <div className={classes.flexRow} style={{ gap: "20px" }}>
              <div className={classes.centeredFlexColumnLimited} style={{ gap: "5px" }}>
                <SettingsCogMultipleRegular fontSize={24} className={classes.centered} />
                <Text style={{ textAlign: "center", alignContent: "flex-start", alignItems: "center", flex: 1 }}>
                  <InfoLabel size="medium" info="Microsoft, Dynamics 365, and Dataverse are trademarks of the Microsoft group of companies. This product is not affiliated with or endorsed by Microsoft.">
                    Built for Microsoft Dataverse integration
                  </InfoLabel>
                </Text>
              </div>
              <div className={classes.centeredFlexColumnLimited} style={{ gap: "5px" }}>
                <TimerRegular fontSize={24} className={classes.centered} />
                <Text style={{ textAlign: "center", alignContent: "flex-start", alignItems: "center", flex: 1 }}>{"Quickstart in < 15 minutes"}</Text>
              </div>
              <div className={classes.centeredFlexColumnLimited} style={{ gap: "5px" }}>
                <AirplaneRegular fontSize={24} className={classes.centered} />
                <Text style={{ textAlign: "center", alignContent: "flex-start", alignItems: "center", flex: 1 }}>Completely offline capable</Text>
              </div>
              <div className={classes.centeredFlexColumnLimited} style={{ gap: "5px" }}>
                <MoneyOffRegular fontSize={24} className={classes.centered} />
                <Text style={{ textAlign: "center", alignContent: "flex-start", alignItems: "center", flex: 1 }}>
                  <InfoLabel
                    size="medium"
                    info={
                      <Text>
                        For learning more about our pricing, visit our <Link as="button" onClick={() => navigate("/docs/general/article/a6bf499e-aab8-406b-84e8-d5bebd653c30")}>pricing article</Link>
                      </Text>
                    }
                  >
                    No upfront costs
                  </InfoLabel>
                </Text>
              </div>
            </div>

            { !globalState.user?.guid && 
              <div className={classes.flexRow} style={{ justifyContent: "center" }}>
                <Button appearance='primary' as="a" href={`/.auth/login/aad?post_login_redirect_uri=${window.location.pathname}`}>Sign up to get started</Button>
              </div>
            }

            <br />

            <Image src='/logo.png' style={{ alignSelf: "center", width: "30%", height: "auto" }} />
            <Text className={classes.subtitle}>
              Why name it Ianus Guard? Ianus is the roman god guarding entrances and exits, just like we guard your applications.
            </Text>

            <br />

            <div className={classes.wrapperContentWithBackground}>
              <div style={{ textAlign: "center" }}>
                <h2>How Ianus works</h2>
                <br />
                <div className={isMobile ? classes.centeredFlexColumn : classes.flexRow} style={{ gap: "20px", marginLeft: "auto", marginRight: "auto" }}>
                  <div className={isMobile ? classes.centeredFlexColumn : classes.centeredFlexColumnLimited}>
                    <BuildingRegular fontSize={36} className={classes.centered} />
                    <h3>Create your publisher</h3>
                    <Text>Define who you are - branding and managing access to your publisher</Text>
                  </div>
                  <div className={isMobile ? classes.centeredFlexColumn : classes.centeredFlexColumnLimited}>
                    <BoxRegular fontSize={36} className={classes.centered} />
                    <h3>Create your products</h3>
                    <Text>Define what you sell - Modules, Solutions, Components</Text>
                  </div>
                  <div className={isMobile ? classes.centeredFlexColumn : classes.centeredFlexColumnLimited}>
                    <BuildingMultipleRegular fontSize={36} className={classes.centered} />
                    <h3>Create your customers</h3>
                    <Text>Define whom you sell to - customer names and their environments</Text>
                  </div>
                  <div className={isMobile ? classes.centeredFlexColumn : classes.centeredFlexColumnLimited}>
                    <DocumentOnePageRegular fontSize={36} className={classes.centered} />
                    <h3>Create licenses</h3>
                    <Text>Issue licenses - defining which customer may use which product in which environment and for how long</Text>
                  </div>
                  <div className={isMobile ? classes.centeredFlexColumn : classes.centeredFlexColumnLimited}>
                    <ApprovalsAppRegular fontSize={36} className={classes.centered} />
                    <h3>Validate</h3>
                    <Text>Our tools will figure out where they are running, which product they are integrated in and whether there is an appropriate, valid license for this combination on your customer's site</Text>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <br />
          
          <Carousel groupSize={1} circular announcement={getAnnouncement}>
            <CarouselSlider>
              {cards.map((card, index) => (
                <BannerCard key={`image-${index}`} card={card} index={index}>
                  { card.text }
                </BannerCard>
              ))}
            </CarouselSlider>
            <CarouselNavContainer
              layout="inline"
              autoplay={{
                "aria-label": "Enable autoplay",
              }}
              next={{ "aria-label": "go to next" }}
              prev={{ "aria-label": "go to prev" }}
            >
              <CarouselNav>
                {(index) => (
                  <CarouselNavButton aria-label={`Carousel Nav Button ${index}`} />
                )}
              </CarouselNav>
            </CarouselNavContainer>
          </Carousel>
        </div>
      </div>
    </div>
  );
};

